import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X, MessageCircle, Clock, Send } from "lucide-react";

/**
 * Customer status-update modal — fires a pre-canned message from a dropdown
 * of common scenarios (waiting for parts, work taking longer, on track,
 * almost done, major delay, etc.) plus a custom free-text option.
 *
 * Live preview of the exact SMS the customer will receive — with name,
 * reg, time interpolated — so the advisor sees the message before sending.
 */
export default function StatusUpdateModal({ open, onClose, job, onSent }) {
  if (!open) return null;
  return (
    <StatusUpdateModalInner
      onClose={onClose}
      job={job}
      onSent={onSent}
    />
  );
}

function StatusUpdateModalInner({ onClose, job, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [templateKey, setTemplateKey] = useState("waiting_parts");
  const [newTime, setNewTime] = useState(job?.estimated_ready_text || "");
  const [customMessage, setCustomMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/status-update-templates").then((r) => {
      if (!cancelled) setTemplates(r.data?.templates || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const active = templates.find((t) => t.key === templateKey);
  const previewSms = active ? renderPreview(active.sms, job, newTime, customMessage) : "";
  const previewEmail = active ? {
    subject: renderPreview(active.email_subject, job, newTime, customMessage),
    body: renderPreview(active.email_body, job, newTime, customMessage),
  } : { subject: "", body: "" };

  const canSend = (() => {
    if (!active) return false;
    if (active.requires_time && !newTime.trim()) return false;
    if (active.key === "custom" && !customMessage.trim()) return false;
    if (!(job?.customer_phone || job?.customer_email)) return false;
    return true;
  })();

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${job.id}/status-update`, {
        template_key: templateKey,
        new_ready_time: newTime.trim() || null,
        custom_message: customMessage.trim() || null,
      });
      const log = r.data?.dispatch_log || [];
      const sent = log.filter((l) => l.ok).map((l) => l.channel.toUpperCase());
      const failed = log.filter((l) => !l.ok).map((l) => l.channel.toUpperCase());
      if (sent.length) {
        toast.success(`Update sent — ${sent.join(" + ")}${failed.length ? ` (${failed.join(", ")} failed)` : ""}`);
        onSent?.(r.data);
        onClose();
      } else {
        toast.error(`Dispatch failed — ${failed.join(", ") || "no channels"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send update");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-foreground/60 backdrop-blur-sm grid place-items-center px-4 py-8 overflow-y-auto"
      onClick={() => !busy && onClose()}
      data-testid="status-update-modal"
    >
      <div
        className="bg-background border border-border rounded-xl shadow-2xl w-full max-w-lg p-6 my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="label-overline text-primary flex items-center gap-1">
              <MessageCircle className="w-3 h-3" /> Customer update
            </div>
            <div className="font-display font-bold text-xl mt-1">Send a status update</div>
            <p className="text-xs text-muted-foreground mt-1">Keep the customer in the loop — pick the right scenario, preview, send.</p>
          </div>
          <button onClick={onClose} disabled={busy} className="text-muted-foreground hover:text-foreground" aria-label="Close" data-testid="status-update-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mt-5 space-y-3">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Scenario</span>
            <select
              value={templateKey}
              onChange={(e) => setTemplateKey(e.target.value)}
              className="input-default mt-1"
              data-testid="status-update-template-select"
            >
              {templates.map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
            {active?.summary && (
              <p className="text-xs text-muted-foreground mt-1">{active.summary}</p>
            )}
          </label>

          {active?.requires_time && (
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" /> New ready time
              </span>
              <input
                type="text"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                placeholder="e.g. 4:30 PM today, tomorrow 10am, 5pm Friday"
                className="input-default mt-1"
                data-testid="status-update-time-input"
                disabled={busy}
              />
            </label>
          )}

          {active?.key === "custom" && (
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Your message</span>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Type your update — the customer will see this verbatim, prefixed with a greeting."
                rows={4}
                className="input-default mt-1"
                data-testid="status-update-custom-input"
                disabled={busy}
              />
            </label>
          )}

          {previewSms && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="status-update-preview">
              <div className="text-[10px] font-bold uppercase tracking-wider text-primary mb-1">SMS preview</div>
              <div className="text-sm leading-relaxed whitespace-pre-wrap">{previewSms}</div>
              <div className="text-[10px] text-muted-foreground mt-2 font-mono-plex">
                {previewSms.length} chars · {Math.ceil(previewSms.length / 160)} SMS
              </div>
              {previewEmail.subject && (
                <details className="mt-3">
                  <summary className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground cursor-pointer">Email preview</summary>
                  <div className="mt-2 text-xs">
                    <div className="font-semibold">Subject: {previewEmail.subject}</div>
                    <div className="mt-1 whitespace-pre-wrap text-muted-foreground">{previewEmail.body}</div>
                  </div>
                </details>
              )}
            </div>
          )}

          <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
            <span className="font-semibold">Will send to:</span>
            {job?.customer_phone && <span data-testid="status-update-to-phone">📱 {job.customer_phone}</span>}
            {job?.customer_email && <span data-testid="status-update-to-email">✉️ {job.customer_email}</span>}
            {!(job?.customer_phone || job?.customer_email) && <span className="text-danger">No contacts — add a phone or email first</span>}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="btn-outline" data-testid="status-update-cancel-btn">Cancel</button>
          <button onClick={send} disabled={busy || !canSend} className="btn-primary" data-testid="status-update-send-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send update
          </button>
        </div>
      </div>
    </div>
  );
}


function renderPreview(template, job, newTime, customMessage) {
  if (!template) return "";
  const firstName = firstNameLite(job?.customer_name);
  const reg = (job?.vehicle_reg || "").toUpperCase();
  const workshop = job?.workshop_name || "your workshop";
  return template
    .replaceAll("{first_name}", firstName)
    .replaceAll("{reg}", reg || "your car")
    .replaceAll("{time}", (newTime || "").trim() || "—")
    .replaceAll("{workshop_name}", workshop)
    .replaceAll("{custom_message}", customMessage || "—");
}

// Lightweight local first-name resolver — mirrors backend `extract_first_name`
// just well enough for the preview. Falls back gracefully on edge cases.
function firstNameLite(full) {
  if (!full) return "there";
  const tokens = full.trim().split(/[\s,]+/).filter(Boolean);
  const titles = new Set(["mr", "mrs", "ms", "miss", "mx", "dr", "prof", "sir", "lady", "lord", "rev"]);
  const multi = tokens.filter((t) => t.length > 1 && !titles.has(t.toLowerCase()));
  if (multi.length >= 2) return titleCase(multi[0]);
  if (multi.length === 1) {
    const title = tokens.find((t) => titles.has(t.toLowerCase()));
    return title ? `${titleCase(title)} ${titleCase(multi[0])}` : titleCase(multi[0]);
  }
  return "there";
}
function titleCase(s) {
  if (!s) return s;
  if (s !== s.toLowerCase() && s !== s.toUpperCase()) return s; // already mixed
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}
